import React, { useMemo } from 'react';
import { AlertTriangle, Lightbulb, Info, CheckCircle } from 'lucide-react';

const weddingRules = [
  {
    id: 'children-no-activities',
    severity: 'warning',
    check: ({ guests, budget, vendors }) => {
      const hasChildren = (guests || []).some(g => g.hasChildren);
      const hasChildcare = (budget?.categories?.childcare || []).length > 0 ||
        (vendors || []).some(v => v.type === 'childcare');
      return hasChildren && !hasChildcare;
    },
    message: "There are children among the guests, but no children's activities or babysitter registered.",
    section: 'guests',
  },
  {
    id: 'outdoor-no-planb',
    severity: 'warning',
    check: ({ formData, planB }) => {
      const isOutdoor = formData.venueType === 'outdoor' || formData.venueType === 'mixed';
      const hasPlanB = planB?.alternativeVenue?.trim() || planB?.tentOption === 'Ja';
      return isOutdoor && !hasPlanB;
    },
    message: 'Outdoor wedding without Plan B. What do you if it rains?',
    section: 'planb',
  },
  {
    id: 'alcohol-no-cooling',
    severity: 'suggestion',
    check: ({ formData, food }) => {
      const hasAlcohol = formData.hasAlcohol === 'yes' || formData.hasAlcohol === 'limited';
      const hasCooling = food?.coolingNotes?.trim();
      return hasAlcohol && !hasCooling;
    },
    message: 'Alcohol is selected, but no plan for ice/cooling is registered.',
    section: 'food',
  },
  {
    id: 'photographer-no-shotlist',
    severity: 'suggestion',
    check: ({ vendors }) => {
      return (vendors || []).some(v => v.type === 'photographer' && v.name?.trim());
    },
    message: 'Photographer er registered - husk a lage en shot list med de viktigste bildene.',
    section: 'vendors',
  },
  {
    id: 'allergies-no-alternative',
    severity: 'warning',
    check: ({ guests, food }) => {
      const hasAllrgyes = (guests || []).some(g => g.allergies?.trim());
      const hasAllrgenInfo = Object.values(food?.courses || {}).some(dishes =>
        (dishes || []).some(d => d.allergens?.trim())
      );
      return hasAllrgyes && !hasAllrgenInfo;
    },
    message: 'Guests have reported allergies but no allergen information is registered on the dishes.',
    section: 'food',
  },
  {
    id: 'guests-exceed-budget',
    severity: 'warning',
    check: ({ formData, guests, budget }) => {
      const estimatedGuests = parseInt(formData.guestCount) || 0;
      const actualGuests = (guests || []).filter(g => g.rsvp !== 'declined').length;
      const totalBudget = parseFloat(budget?.totalBudget) || 0;
      return actualGuests > estimatedGuests && estimatedGuests > 0 && totalBudget > 0;
    },
    message: 'Number of confirmed guests exceeds the original estimate. Budget may need adjusting.',
    section: 'budget',
  },
  {
    id: 'vendor-no-deposit-deadline',
    severity: 'suggestion',
    check: ({ vendors }) => {
      return (vendors || []).some(v => v.name?.trim() && !v.deadline && !v.deposit);
    },
    message: 'One or more vendors are missing payment deadline and deposit information.',
    section: 'vendors',
  },
  {
    id: 'memorial-not-in-plan',
    severity: 'info',
    check: ({ memorials }) => {
      return (memorials || []).some(m => m.name?.trim() && (m.tributes || []).length > 0 &&
        (m.tributes || []).some(t => ['reserved-seat', 'memory-table', 'candle', 'dedicated-song'].includes(t))
      );
    },
    message: 'You are honoring special people – remember to add details in decor, timeline and checklist.',
    section: 'memorial',
  },
  {
    id: 'buffet-no-heating',
    severity: 'suggestion',
    check: ({ food }) => {
      return food?.cateringType === 'buffet' && !food?.heatingNotes?.trim();
    },
    message: 'Buffet er selected – has dere en plan for varmehold av maten?',
    section: 'food',
  },
  {
    id: 'children-no-menu',
    severity: 'warning',
    check: ({ guests, food }) => {
      const hasChildren = (guests || []).some(g => g.hasChildren);
      return hasChildren && !food?.childrenMenu?.trim();
    },
    message: 'Children are registered among the guests, but no kids menu is planned.',
    section: 'food',
  },
  {
    id: 'no-vendor-meals',
    severity: 'suggestion',
    check: ({ vendors, food }) => {
      const hasVendors = (vendors || []).filter(v => v.name?.trim()).length > 0;
      return hasVendors && !(parseInt(food?.vendorMeals) > 0);
    },
    message: 'You have vendors registered but no vendor meals planned.',
    section: 'food',
  },
  {
    id: 'accessibility-no-transport',
    severity: 'warning',
    check: ({ guests }) => {
      return (guests || []).some(g => g.accessibility?.trim() && !g.transportNeed);
    },
    message: 'A guest has accessibility needs but no transport needs are marked. Double check this.',
    section: 'guests',
  },
  {
    id: 'guests-not-seated',
    severity: 'warning',
    check: ({ guests, seating }) => {
      const validGuests = (guests || []).filter(g => g.name?.trim() && g.rsvp !== 'declined');
      if (validGuests.length === 0) return false;
      const tables = seating?.tables || [];
      if (tables.length === 0) return validGuests.length > 5;
      const assigned = new Set();
      tables.forEach(t => Object.values(t.seats || {}).forEach(s => { if (s?.guestIndex != null) assigned.add(s.guestIndex); }));
      const unplaced = validGuests.filter((_, i) => !assigned.has(i)).length;
      return unplaced > 0;
    },
    message: 'Some guests are missing table seats. Go to seating chart to seat everyone.',
    section: 'seating',
  },
  {
    id: 'seating-overcapacity',
    severity: 'warning',
    check: ({ seating }) => {
      const tables = seating?.tables || [];
      return tables.some(t => {
        const count = Object.values(t.seats || {}).filter(Boolean).length;
        return count > t.capacity;
      });
    },
    message: 'Et or flere tables have more guests enn kapasiteten tilsier.',
    section: 'seating',
  },
  // ─── New module rules ───
  {
    id: 'no-timeline',
    severity: 'warning',
    check: ({ timeline }) => {
      return !(timeline?.events?.length > 0);
    },
    message: 'No timeline has been created. A day timeline keeps everyone on track.',
    section: 'timeline',
  },
  {
    id: 'timeline-no-ceremony',
    severity: 'warning',
    check: ({ timeline }) => {
      if (!(timeline?.events?.length > 0)) return false;
      return !timeline.events.some(e => e.label?.toLowerCase().includes('seremoni') || e.category === 'ceremony');
    },
    message: 'Tidslinjen mangler en seremoni-hendelse.',
    section: 'timeline',
  },
  {
    id: 'kids-no-babysitter',
    severity: 'suggestion',
    check: ({ guests, kids }) => {
      const hasKids = (guests || []).some(g => g.hasChildren);
      return hasKids && kids?.hasBabysitter !== 'Ja' && !(kids?.children?.length > 3);
    },
    message: "There are children at the wedding, but no babysitter is planned. Consider if it's needed.",
    section: 'kids',
  },
  {
    id: 'kids-no-entertainment',
    severity: 'suggestion',
    check: ({ guests, kids }) => {
      const hasKids = (guests || []).some(g => g.hasChildren);
      return hasKids && !(kids?.activities?.length > 0);
    },
    message: 'Children are registered but no activities planned. Boredom may occur!',
    section: 'kids',
  },
  {
    id: 'ceremony-no-officiant',
    severity: 'warning',
    check: ({ ceremony }) => {
      return ceremony?.ceremonyType && !ceremony?.officiantName?.trim();
    },
    message: 'Ceremony type is selected, but no officiant is registered.',
    section: 'ceremony-detail',
  },
  {
    id: 'ceremony-no-rings-holder',
    severity: 'suggestion',
    check: ({ ceremony }) => {
      return ceremony?.ceremonyType && !ceremony?.ringsHeldBy?.trim();
    },
    message: 'No one is assigned responsibility for the rings. Important to clarify!',
    section: 'ceremony-detail',
  },
  {
    id: 'no-transport',
    severity: 'suggestion',
    check: ({ transport }) => {
      return !(transport?.rides?.length > 0);
    },
    message: 'No transport is planned. Remember transport to ceremony, reception and home.',
    section: 'transport',
  },
  {
    id: 'guests-need-transport',
    severity: 'warning',
    check: ({ guests, transport }) => {
      const needTransport = (guests || []).filter(g => g.transportNeed).length;
      return needTransport > 0 && !(transport?.rides?.length > 0);
    },
    message: 'Guests have marked transport needs, but no transport is planned.',
    section: 'transport',
  },
  {
    id: 'no-responsibilities',
    severity: 'suggestion',
    check: ({ responsibilities }) => {
      return !(responsibilities?.tasks?.length > 0);
    },
    message: 'No tasks are assigned. Use the responsibility tracker to avoid chaos on the day.',
    section: 'responsibilities',
  },
  {
    id: 'no-day-of-contact',
    severity: 'warning',
    check: ({ responsibilities }) => {
      return (responsibilities?.tasks?.length > 0) && !responsibilities?.dayOfContact?.name?.trim();
    },
    message: 'Tasks are assigned, but no day-of contact person is set.',
    section: 'responsibilities',
  },
  {
    id: 'no-emergency-kit',
    severity: 'suggestion',
    check: ({ emergency }) => {
      return !(emergency?.categories?.length > 0);
    },
    message: 'No emergency kit is prepared. It can save the day!',
    section: 'emergency',
  },
  {
    id: 'budget-no-buffer',
    severity: 'suggestion',
    check: ({ budget }) => {
      return (parseFloat(budget?.totalBudget) > 0) && !(parseFloat(budget?.bufferPercent) > 0) && !(parseFloat(budget?.bufferAmount) > 0);
    },
    message: 'The budget has no buffer for unexpected costs. 5-10% is recommended.',
    section: 'budget',
  },
  {
    id: 'drink-no-quantity',
    severity: 'suggestion',
    check: ({ food }) => {
      const drinks = food?.drinks || {};
      const hasSelection = Object.values(drinks).some(d => parseFloat(d.quantity) > 0);
      return food?.cateringType && !hasSelection;
    },
    message: 'Alcohol/drinks are not planned in detail. Remember to set up quantities.',
    section: 'food',
  },
];

export const analyzeWeddingPlan = (data) => {
  return weddingRules
    .filter(rule => { try { return rule.check(data); } catch { return false; } })
    .map(({ id, severity, message, section }) => ({ id, severity, message, section }));
};

const severityConfig = {
  warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200/60' },
  suggestion: { icon: Lightbulb, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200/60' },
  info: { icon: Info, color: 'text-stone-500', bg: 'bg-stone-50', border: 'border-stone-200' },
};

export const ForgottenAnalyzer = ({ formData, templateData, onNavigateToSection }) => {
  const findings = useMemo(() => analyzeWeddingPlan({
    formData: formData || {},
    guests: templateData?.guests || [],
    budget: templateData?.budget || {},
    music: templateData?.music || {},
    vendors: templateData?.vendors || [],
    memorials: templateData?.memorials || [],
    food: templateData?.food || {},
    planB: templateData?.planB || {},
    seating: templateData?.seating || {},
    timeline: templateData?.timeline || {},
    ceremony: templateData?.ceremony || {},
    kids: templateData?.kids || {},
    transport: templateData?.transport || {},
    responsibilities: templateData?.responsibilities || {},
    emergency: templateData?.emergency || {},
  }), [formData, templateData]);

  if (findings.length === 0) {
    return (
      <div data-testid="forgotten-analyzer" className="bg-green-50 rounded-2xl border border-green-200/60 p-6 text-center">
        <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-3" />
        <h3 className="font-sans font-semibold text-green-800 text-sm mb-1">Alt ser bra ut!</h3>
        <p className="text-xs font-sans text-green-600">We found no gaps in your wedding plan.</p>
      </div>
    );
  }

  return (
    <div data-testid="forgotten-analyzer" className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <h3 className="font-sans font-semibold text-stone-800 text-sm">Har du husket dette?</h3>
        <span className="text-[10px] font-sans font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{findings.length}</span>
      </div>
      {findings.map(finding => {
        const config = severityConfig[finding.severity] || severityConfig.info;
        const Icon = config.icon;
        return (
          <div key={finding.id}
            className={`flex items-start gap-3 rounded-xl ${config.bg} border ${config.border} px-4 py-3 cursor-pointer hover:shadow-sm transition-shadow`}
            onClick={() => onNavigateToSection?.(finding.section)}>
            <Icon className={`w-4 h-4 ${config.color} flex-shrink-0 mt-0.5`} />
            <p className="text-xs font-sans text-stone-700 leading-relaxed">{finding.message}</p>
          </div>
        );
      })}
    </div>
  );
};
